import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type BlockTipo = 'markdown' | 'todo' | 'quote' | 'code' | 'link';

export interface LousaBlock {
  id: number;
  lousa_id: number;
  tipo: BlockTipo;
  conteudo: string;
  ordem: number;
  metadata_json: string | null;
  criado_em: string;
  atualizado_em: string;
}

export interface Lousa {
  id: number;
  slug: string;
  titulo: string;
  descricao: string | null;
  cor: string | null;
  tags: string | null;
  arquivada: number;
  fixada: number;
  criado_em: string;
  atualizado_em: string;
}

export interface LousaWithBlocks extends Lousa {
  blocks: LousaBlock[];
}

export interface LousaStats {
  total: number;
  ativas: number;
  arquivadas: number;
  total_blocks: number;
}

export function useLousas(params: { arquivadas?: boolean } = {}): UseQueryResult<{
  lousas: Lousa[];
  stats: LousaStats;
}> {
  const qs = params.arquivadas ? '?arquivadas=1' : '';
  return useQuery({
    queryKey: ['lousas', 'list', params],
    queryFn: () => api<{ lousas: Lousa[]; stats: LousaStats }>(`/api/lousas${qs}`),
  });
}

export function useLousa(slug: string | null): UseQueryResult<LousaWithBlocks> {
  return useQuery({
    queryKey: ['lousas', 'detail', slug],
    enabled: !!slug,
    queryFn: () => api<LousaWithBlocks>(`/api/lousas/${slug}`),
  });
}

export function useCreateLousa(): ReturnType<
  typeof useMutation<
    Lousa,
    Error,
    { titulo: string; descricao?: string | null; cor?: string | null; tags?: string[] | null }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api<Lousa>('/api/lousas', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lousas'] }),
  });
}

export function useUpdateLousa(): ReturnType<
  typeof useMutation<
    Lousa,
    Error,
    {
      slug: string;
      patch: {
        titulo?: string;
        descricao?: string | null;
        cor?: string | null;
        tags?: string[] | null;
        arquivada?: boolean;
        fixada?: boolean;
      };
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, patch }) =>
      api<Lousa>(`/api/lousas/${slug}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lousas'] }),
  });
}

export function useDeleteLousa(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      api(`/api/lousas/${slug}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lousas'] }),
  });
}

export function useAddBlock(): ReturnType<
  typeof useMutation<
    LousaBlock,
    Error,
    {
      slug: string;
      block: { tipo?: BlockTipo; conteudo: string; metadata?: Record<string, unknown> | null };
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, block }) =>
      api<LousaBlock>(`/api/lousas/${slug}/blocks`, { method: 'POST', body: block }),
    onSuccess: (_, { slug }) => {
      qc.invalidateQueries({ queryKey: ['lousas', 'detail', slug] });
      qc.invalidateQueries({ queryKey: ['lousas', 'list'] });
    },
  });
}

export function useUpdateBlock(): ReturnType<
  typeof useMutation<
    LousaBlock,
    Error,
    {
      id: number;
      patch: { tipo?: BlockTipo; conteudo?: string; metadata?: Record<string, unknown> | null };
      slug: string;
    }
  >
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) =>
      api<LousaBlock>(`/api/lousas/blocks/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (_, { slug }) => {
      qc.invalidateQueries({ queryKey: ['lousas', 'detail', slug] });
    },
  });
}

export function useDeleteBlock(): ReturnType<
  typeof useMutation<void, Error, { id: number; slug: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) =>
      api(`/api/lousas/blocks/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: (_, { slug }) => {
      qc.invalidateQueries({ queryKey: ['lousas', 'detail', slug] });
    },
  });
}

export function useReorderBlocks(): ReturnType<
  typeof useMutation<{ ok: true }, Error, { slug: string; order: number[] }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, order }) =>
      api<{ ok: true }>(`/api/lousas/${slug}/reorder`, {
        method: 'POST',
        body: { order },
      }),
    onSuccess: (_, { slug }) => {
      qc.invalidateQueries({ queryKey: ['lousas', 'detail', slug] });
    },
  });
}
